import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function CoupleSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="couple" className="relative py-24 md:py-36 bg-midnight overflow-hidden">
      {/* Floating particles effect overlay */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-copper/20"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0.15, 0.4, 0.15],
            }}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.8,
            }}
          />
        ))}
      </div>

      <div className="max-w-5xl mx-auto px-6" ref={ref}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="font-sans text-xs uppercase tracking-[3px] text-copper/60 mb-3">
            Meet the Couple
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-cream font-semibold">
            The Bride &amp; Groom
          </h2>
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className="w-12 md:w-20 h-px bg-copper/20" />
            <div className="w-1.5 h-1.5 rotate-45 bg-copper/40" />
            <span className="w-12 md:w-20 h-px bg-copper/20" />
          </div>
        </motion.div>

        {/* Couple cards */}
        <div className="flex flex-col md:flex-row items-stretch gap-8 md:gap-0">
          {/* Groom */}
          <motion.div
            initial={{ opacity: 0, x: -60, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="flex-1 bg-ink-light/40 rounded-2xl p-8 md:p-12 border border-copper/5 relative backdrop-blur-sm hover:border-copper/15 transition-all duration-500"
          >
            <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-copper/10 rounded-tl-lg" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-copper/10 rounded-br-lg" />

            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-copper/10 mb-6">
                <i className="ri-user-3-line text-xl text-copper w-6 h-6 flex items-center justify-center" />
              </div>

              <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-cream font-semibold mb-2">
                Sharoof C Majeed
              </h3>
              <p className="font-serif text-base italic text-copper/70 mb-8">Son of</p>

              <div className="space-y-3">
                <p className="font-sans text-base md:text-lg text-pearl font-medium">
                  Mr. Abdul Majeed
                </p>
                <p className="font-sans text-base md:text-lg text-pearl font-medium">
                  Mrs. Zeenath
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-copper/10">
                <div className="flex items-center gap-2 text-pearl/50 text-sm">
                  <i className="ri-map-pin-2-line w-4 h-4 flex items-center justify-center" />
                  <span>Chakkungal (H), Muvattupuzha</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Center divider */}
          <div className="hidden md:flex items-center justify-center px-8">
            <div className="relative flex flex-col items-center">
              <div className="w-px h-24 bg-gradient-to-b from-transparent via-copper/20 to-transparent" />
              <div className="w-12 h-12 rounded-full bg-copper/10 border border-copper/20 flex items-center justify-center my-4">
                <span className="font-display text-lg text-copper">&amp;</span>
              </div>
              <div className="w-px h-24 bg-gradient-to-b from-transparent via-copper/20 to-transparent" />
            </div>
          </div>

          {/* Bride */}
          <motion.div
            initial={{ opacity: 0, x: 60, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
            className="flex-1 bg-ink-light/40 rounded-2xl p-8 md:p-12 border border-copper/5 relative backdrop-blur-sm hover:border-copper/15 transition-all duration-500"
          >
            <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-copper/10 rounded-tr-lg" />
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-copper/10 rounded-bl-lg" />

            <div className="text-center md:text-right">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-copper/10 mb-6">
                <i className="ri-women-line text-xl text-copper w-6 h-6 flex items-center justify-center" />
              </div>

              <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-cream font-semibold mb-2">
                Adhabiya Shekkeer Ali
              </h3>
              <p className="font-serif text-base italic text-copper/70 mb-8">Daughter of</p>

              <div className="space-y-3">
                <p className="font-sans text-base md:text-lg text-pearl font-medium">
                  Mr. Shekkeer Ali
                </p>
                <p className="font-sans text-base md:text-lg text-pearl font-medium">
                  Mrs. Raseena
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-copper/10">
                <div className="flex items-center justify-end gap-2 text-pearl/50 text-sm">
                  <span>Thachiyath (H), Kothamangalam</span>
                  <i className="ri-map-pin-2-line w-4 h-4 flex items-center justify-center" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}